import { useMemo, useState, useEffect, useCallback } from 'react';

import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import { useTheme } from '@mui/material/styles';

import { usePathname, useSearchParams } from 'src/routes/hooks';

import { NavList } from './nav-list';
import { navSectionClasses } from '../classes';
import { navSectionCssVars } from '../css-vars';
import { NavUl, NavLi, Subheader } from '../styles';
import { findActiveNavItemPath } from '../tree-utils';

// ----------------------------------------------------------------------

const NAV_GROUP_STATE_STORAGE_KEY = 'rado:dashboard-navigation:open-groups:v1';

function readGroupOpenState(groupKey, fallback) {
  if (!groupKey || typeof window === 'undefined') return fallback;

  try {
    const stored = JSON.parse(window.localStorage.getItem(NAV_GROUP_STATE_STORAGE_KEY) ?? '{}');
    return typeof stored[groupKey] === 'boolean' ? stored[groupKey] : fallback;
  } catch {
    return fallback;
  }
}

function writeGroupOpenState(groupKey, open) {
  if (!groupKey || typeof window === 'undefined') return;

  try {
    const stored = JSON.parse(window.localStorage.getItem(NAV_GROUP_STATE_STORAGE_KEY) ?? '{}');
    window.localStorage.setItem(
      NAV_GROUP_STATE_STORAGE_KEY,
      JSON.stringify({ ...stored, [groupKey]: open })
    );
  } catch {
    // محدودیت فضای ذخیره‌سازی مرورگر نباید مانع استفاده از منو شود.
  }
}

// ----------------------------------------------------------------------

export function NavSectionVertical({
  sx,
  data,
  render,
  slotProps,
  enabledRootRedirect,
  cssVars: overridesVars,
}) {
  const theme = useTheme();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const rootItems = useMemo(() => data.flatMap((group) => group.items), [data]);
  const activeRootPath = findActiveNavItemPath(pathname, searchParams, rootItems);
  const activeRootHasChildren = rootItems.find((item) => item.path === activeRootPath)?.children;

  const [openRootPath, setOpenRootPath] = useState(activeRootHasChildren ? activeRootPath : null);

  useEffect(() => {
    setOpenRootPath(activeRootHasChildren ? activeRootPath : null);
  }, [activeRootHasChildren, activeRootPath]);

  const handleToggleRoot = useCallback((path) => {
    setOpenRootPath((currentPath) => (currentPath === path ? null : path));
  }, []);

  const cssVars = {
    ...navSectionCssVars.vertical(theme),
    ...overridesVars,
  };

  return (
    <Stack component="nav" className={navSectionClasses.vertical.root} sx={{ ...cssVars, ...sx }}>
      <NavUl sx={{ flex: '1 1 auto', gap: 'var(--nav-item-gap)' }}>
        {data.map((group) => (
          <Group
            key={group.subheader ?? group.items[0].title}
            groupKey={group.key ?? group.subheader}
            subheader={group.subheader}
            defaultOpen={group.defaultOpen}
            items={group.items}
            render={render}
            slotProps={slotProps}
            enabledRootRedirect={enabledRootRedirect}
            activeRootPath={activeRootPath}
            openRootPath={openRootPath}
            onToggleRoot={handleToggleRoot}
          />
        ))}
      </NavUl>
    </Stack>
  );
}

// ----------------------------------------------------------------------

function Group({
  items,
  render,
  groupKey,
  subheader,
  defaultOpen = false,
  slotProps,
  enabledRootRedirect,
  activeRootPath,
  openRootPath,
  onToggleRoot,
}) {
  const isActive = items.some((item) => item.path === activeRootPath);
  const [open, setOpen] = useState(() => readGroupOpenState(groupKey, defaultOpen || isActive));

  useEffect(() => {
    if (isActive) setOpen(true);
    if (isActive) writeGroupOpenState(groupKey, true);
  }, [groupKey, isActive]);

  const handleToggle = useCallback(() => {
    setOpen((previous) => {
      const next = !previous;
      writeGroupOpenState(groupKey, next);
      return next;
    });
  }, [groupKey]);

  const renderContent = (
    <NavUl sx={{ gap: 'var(--nav-item-gap)' }}>
      {items.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={1}
          slotProps={slotProps}
          enabledRootRedirect={enabledRootRedirect}
          selected={activeRootPath === list.path}
          openMenu={openRootPath === list.path}
          onToggleMenu={onToggleRoot}
        />
      ))}
    </NavUl>
  );

  return (
    <NavLi>
      {subheader ? (
        <>
          <Subheader
            data-title={subheader}
            open={open}
            onClick={handleToggle}
            sx={slotProps?.subheader}
          >
            {subheader}
          </Subheader>

          <Collapse in={open}>{renderContent}</Collapse>
        </>
      ) : (
        renderContent
      )}
    </NavLi>
  );
}
